import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../utils/idbUtils";

const DB_NAME = "grpc_tareas_ejecucion";
const DB_VERSION = 4;
const STORE_EJECUCIONES = "ejecuciones";
const STORE_SYNC = "sync";
/** Recorridos (geometrías en WKB) por ejecución, pedidos por día. */
const STORE_RECORRIDOS = "recorridos";

/** Item mínimo que necesita el merge de delta (mismo contrato que
 *  `GrpcLoteItem`/`GrpcAplicacionItem`: watermark por finca, no uno global). */
export type GrpcTareaEjecucionItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
};

/** Recorrido guardado de una ejecución, tal como llega del servidor (WKB). */
export type StoredTareaEjecucionRecorrido = {
  /** Id de la ejecución. */
  id: string;
  /** Última actualización de la ejecución al bajar el recorrido (Unix ms). */
  ultimaActualizacion: number;
  /** Líneas de muestras automáticas, WKB MultiLineString. */
  muestrasAutomaticas: Uint8Array;
  /** Puntos de la ejecución, WKB MultiPoint, o null si no hay. */
  ejecucion: Uint8Array | null;
};

/** IndexedDB **nueva y separada** para las ejecuciones de tarea de rastro que
 *  trae `TareaEjecucionGrpcService` (ver CLAUDE.md del Server, sección
 *  migración a gRPC-Web) — mismo patrón que `GrpcLoteDatabase`/
 *  `GrpcAplicacionDatabase`: delta real por `fincaId`. */
class GrpcTareaEjecucionDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 4
   *  (2026-09-30) separó los recorridos de las ejecuciones: lo guardado antes
   *  traía las geometrías en GeoJSON, así que un navegador con una versión
   *  anterior vacía la base una sola vez y la vuelve a llenar. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_EJECUCIONES, clave: "id", indices: ["fincaId"] },
      { nombre: STORE_SYNC, clave: "fincaId" },
      { nombre: STORE_RECORRIDOS, clave: "id" },
    ],
    { vaciarAntesDe: 4 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  async getUltimaSincronizacionAsync(fincaId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ fincaId: string; ultimaSincronizacion: string } | undefined>(
      store.get(fincaId),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  async putDeltaAsync<T extends GrpcTareaEjecucionItem>(
    fincaId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_EJECUCIONES, STORE_SYNC, STORE_RECORRIDOS], "readwrite");
    const store = tx.objectStore(STORE_EJECUCIONES);
    const syncStore = tx.objectStore(STORE_SYNC);
    const recorridos = tx.objectStore(STORE_RECORRIDOS);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
        recorridos.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ fincaId, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  async getListByFincaIdAsync<T>(fincaId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_EJECUCIONES, "readonly")
      .objectStore(STORE_EJECUCIONES)
      .index("fincaId");
    return reqToPromise<T[]>(index.getAll(fincaId));
  }

  /** Actualiza (o inserta) una sola ejecución sin tocar el resto — lo llaman
   *  los guardados puntuales (save/asignar-tarea) para que los consumidores
   *  vean el cambio sin esperar al próximo delta. */
  async putOneAsync<T extends GrpcTareaEjecucionItem>(item: T): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_EJECUCIONES, "readwrite");
    tx.objectStore(STORE_EJECUCIONES).put(item);
    await txDone(tx);
  }

  /** Borra una ejecución puntual (baja confirmada por el server) y su
   *  recorrido. */
  async deleteOneAsync(id: string): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_EJECUCIONES, STORE_RECORRIDOS], "readwrite");
    tx.objectStore(STORE_EJECUCIONES).delete(id);
    tx.objectStore(STORE_RECORRIDOS).delete(id);
    await txDone(tx);
  }

  /** Recorridos guardados de las ejecuciones pedidas; las que no tienen
   *  recorrido guardado no aparecen. */
  async getRecorridosAsync(ids: readonly string[]): Promise<StoredTareaEjecucionRecorrido[]> {
    const db = await this.open();
    const store = db.transaction(STORE_RECORRIDOS, "readonly").objectStore(STORE_RECORRIDOS);
    const filas = await Promise.all(
      ids.map((id) => reqToPromise<StoredTareaEjecucionRecorrido | undefined>(store.get(id))),
    );
    return filas.filter((fila): fila is StoredTareaEjecucionRecorrido => fila !== undefined);
  }

  /** Guarda o reemplaza recorridos recién bajados del servidor. */
  async putRecorridosAsync(recorridos: readonly StoredTareaEjecucionRecorrido[]): Promise<void> {
    if (recorridos.length === 0) return;
    const db = await this.open();
    const tx = db.transaction(STORE_RECORRIDOS, "readwrite");
    const store = tx.objectStore(STORE_RECORRIDOS);
    for (const recorrido of recorridos) {
      store.put(recorrido);
    }
    await txDone(tx);
  }
}

/** Singleton de la base de ejecuciones de tarea de rastro por gRPC-Web (con
 *  delta por finca). */
export const grpcTareaEjecucionDatabase = new GrpcTareaEjecucionDatabase();
